import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, CheckCircle, Mail, User, ArrowLeft } from 'lucide-react';
import { services } from '../data/mock';

export const ServicePage = () => {
  const { serviceType } = useParams();
  
  // Map URL params to service data
  const serviceMap = {
    'business-consulting': 0,
    'business-civil-rights-consulting': 0,
    'h-r-civil-rights-consulting': 0,
    'it-cybersecurity': 1,
    'it-cybersecurity-data-analytics': 1,
    'it-cybersecurity-risk-management': 1,
    'cybersecurity-governance-risk-and-compliance-grc': 1,
    'cyber-security-governance-risk-and-compliance': 1,
    'design-ai': 2,
    'graphic-design-ai-integration': 2
  };
  
  const serviceIndex = serviceMap[serviceType];
  const service = services[serviceIndex];
  
  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Service Not Found</h1>
          <Link to="/">
            <Button>Return Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Service Hero */}
      <section className="relative py-20 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-4">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Link>
              
              <div>
                {/* Service Level badge removed */}
                <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                  {service.title}
                </h1>
                <p className="text-xl text-blue-600 font-semibold mb-6">
                  {service.subtitle}
                </p>
                <p className="text-lg text-gray-600 leading-relaxed">
                  {service.description}
                </p>
              </div>
              
              <Link to="/contact">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white">
                  Schedule Consultation
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
            
            <div className="relative">
              <img
                src={service.image}
                alt={`${service.title} consultation`}
                className="w-full h-96 lg:h-[500px] object-cover rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Service Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Features List */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                What We Offer
              </h2>
              <div className="space-y-4">
                {service.features.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="h-6 w-6 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700 font-medium">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* About Our Consultant section removed */}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Why Choose Our {service.title} Services?
          </h2>
          
          <div className={`grid grid-cols-1 ${(service.id === 1 || service.id === 3) ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-8 mt-12`}>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Expert Knowledge</h3>
              <p className="text-gray-600">Industry-certified professionals with proven track records</p>
            </div>
            
            {/* Show Remote Flexibility for H.R. & Civil Rights (Service Level 1) and Design & AI (Service Level 3) */}
            {(service.id === 1 || service.id === 3) && (
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Remote Flexibility</h3>
                <p className="text-gray-600">Efficient service delivery with modern remote capabilities</p>
              </div>
            )}
            
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Comprehensive Support</h3>
              <p className="text-gray-600">
                {service.id === 2 
                  ? "All work is performed remotely to maintain cost efficiency and provide flexibility for both project delivery and client collaboration."
                  : "End-to-end solutions tailored to your specific needs"
                }
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-700">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Ready to Get Started with {service.title}?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Schedule a consultation to discuss your specific needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
                Schedule Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <a href="mailto:info@skillspectrumconsulting.net">
              <Button variant="outline" size="lg" className="text-white border-white hover:bg-white hover:text-blue-600">
                Email Us
                <Mail className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};